'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import LoadingGif from '@/components/ui/LoadingGif';

type BlockingTaskDescriptor = {
  message: string;
  createdAt: number;
};

type NavigationLoaderContextValue = {
  isNavigating: boolean;
  isBlocking: boolean;
  startNavigation: () => void;
  stopNavigation: () => void;
  setBlockingTask: (key: string, active: boolean, message?: string) => void;
  runBlockingTask: <T>(message: string, task: () => Promise<T>) => Promise<T>;
};

const NavigationLoaderContext = createContext<NavigationLoaderContextValue>({
  isNavigating: false,
  isBlocking: false,
  startNavigation: () => {},
  stopNavigation: () => {},
  setBlockingTask: () => {},
  runBlockingTask: async <T,>(message: string, task: () => Promise<T>) => task(),
});

export function NavigationLoaderProvider({ children }: { children: React.ReactNode }) {
  const { t } = useLanguage();
  const [isNavigating, setIsNavigating] = useState(false);
  const [showNavigationOverlay, setShowNavigationOverlay] = useState(false);
  const [blockingTasks, setBlockingTasks] = useState<Record<string, BlockingTaskDescriptor>>({});
  const [showBlockingOverlay, setShowBlockingOverlay] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);
  const showTimeoutRef = useRef<number | null>(null);
  const overlayTimeoutRef = useRef<number | null>(null);
  const overlayHideTimeoutRef = useRef<number | null>(null);
  const blockingShowTimeoutRef = useRef<number | null>(null);
  const blockingHideTimeoutRef = useRef<number | null>(null);
  const blockingVisibleAtRef = useRef<number | null>(null);
  const blockingTaskSeqRef = useRef(0);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current !== null) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const clearShowTimeout = useCallback(() => {
    if (showTimeoutRef.current !== null) {
      window.clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
  }, []);

  const clearOverlayTimeout = useCallback(() => {
    if (overlayTimeoutRef.current !== null) {
      window.clearTimeout(overlayTimeoutRef.current);
      overlayTimeoutRef.current = null;
    }
  }, []);

  const clearOverlayHideTimeout = useCallback(() => {
    if (overlayHideTimeoutRef.current !== null) {
      window.clearTimeout(overlayHideTimeoutRef.current);
      overlayHideTimeoutRef.current = null;
    }
  }, []);

  const clearBlockingShowTimeout = useCallback(() => {
    if (blockingShowTimeoutRef.current !== null) {
      window.clearTimeout(blockingShowTimeoutRef.current);
      blockingShowTimeoutRef.current = null;
    }
  }, []);

  const clearBlockingHideTimeout = useCallback(() => {
    if (blockingHideTimeoutRef.current !== null) {
      window.clearTimeout(blockingHideTimeoutRef.current);
      blockingHideTimeoutRef.current = null;
    }
  }, []);

  const startNavigation = useCallback(() => {
    clearHideTimeout();
    clearShowTimeout();
    clearOverlayTimeout();
    clearOverlayHideTimeout();
    showTimeoutRef.current = window.setTimeout(() => {
      setIsNavigating(true);
      showTimeoutRef.current = null;
    }, 80);
    overlayTimeoutRef.current = window.setTimeout(() => {
      setShowNavigationOverlay(true);
      overlayTimeoutRef.current = null;
    }, 850);
  }, [clearHideTimeout, clearOverlayHideTimeout, clearOverlayTimeout, clearShowTimeout]);

  const stopNavigation = useCallback(() => {
    clearShowTimeout();
    clearOverlayTimeout();
    clearHideTimeout();
    clearOverlayHideTimeout();
    overlayHideTimeoutRef.current = window.setTimeout(() => {
      setShowNavigationOverlay(false);
      overlayHideTimeoutRef.current = null;
    }, 0);
    hideTimeoutRef.current = window.setTimeout(() => {
      setIsNavigating(false);
      hideTimeoutRef.current = null;
    }, 140);
  }, [clearHideTimeout, clearOverlayHideTimeout, clearOverlayTimeout, clearShowTimeout]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const nextUrl = new URL(anchor.href, window.location.href);
      if (nextUrl.origin !== window.location.origin) return;

      const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const targetUrl = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;

      if (currentUrl === targetUrl) return;
      startNavigation();
    };

    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [startNavigation]);

  useEffect(() => {
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    window.history.pushState = function pushState(...args) {
      const result = originalPushState(...args);
      stopNavigation();
      return result;
    };

    window.history.replaceState = function replaceState(...args) {
      const result = originalReplaceState(...args);
      stopNavigation();
      return result;
    };

    const handlePopState = () => {
      stopNavigation();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [stopNavigation]);

  const setBlockingTask = useCallback((key: string, active: boolean, message?: string) => {
    setBlockingTasks((current) => {
      if (!active) {
        if (!(key in current)) return current;
        const next = { ...current };
        delete next[key];
        return next;
      }

      const nextMessage = message?.trim() || t('common.loading');
      const existing = current[key];
      return {
        ...current,
        [key]: {
          message: nextMessage,
          createdAt: existing?.createdAt || ++blockingTaskSeqRef.current,
        },
      };
    });
  }, [t]);

  const runBlockingTask = useCallback(async <T,>(message: string, task: () => Promise<T>) => {
    const key = `blocking-task-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setBlockingTask(key, true, message);
    try {
      return await task();
    } finally {
      setBlockingTask(key, false);
    }
  }, [setBlockingTask]);

  const blockingTaskList = useMemo(() => Object.values(blockingTasks), [blockingTasks]);
  const isBlocking = blockingTaskList.length > 0;
  const blockingMessage = useMemo(() => {
    if (blockingTaskList.length === 0) return t('common.loading');
    return [...blockingTaskList].sort((left, right) => right.createdAt - left.createdAt)[0]?.message || t('common.loading');
  }, [blockingTaskList, t]);

  useEffect(() => {
    clearBlockingShowTimeout();
    clearBlockingHideTimeout();

    if (isBlocking) {
      if (showBlockingOverlay) return;
      blockingShowTimeoutRef.current = window.setTimeout(() => {
        blockingVisibleAtRef.current = Date.now();
        setShowBlockingOverlay(true);
        blockingShowTimeoutRef.current = null;
      }, 140);
      return;
    }

    if (!showBlockingOverlay) return;
    const visibleFor = blockingVisibleAtRef.current ? Date.now() - blockingVisibleAtRef.current : 0;
    const remaining = Math.max(0, 260 - visibleFor);
    blockingHideTimeoutRef.current = window.setTimeout(() => {
      setShowBlockingOverlay(false);
      blockingVisibleAtRef.current = null;
      blockingHideTimeoutRef.current = null;
    }, remaining);
  }, [clearBlockingHideTimeout, clearBlockingShowTimeout, isBlocking, showBlockingOverlay]);

  useEffect(() => {
    return () => {
      clearShowTimeout();
      clearHideTimeout();
    };
  }, [clearHideTimeout, clearShowTimeout]);

  useEffect(() => {
    return () => {
      clearOverlayTimeout();
    };
  }, [clearOverlayTimeout]);

  useEffect(() => {
    return () => {
      clearOverlayHideTimeout();
    };
  }, [clearOverlayHideTimeout]);

  useEffect(() => {
    return () => {
      clearBlockingShowTimeout();
      clearBlockingHideTimeout();
    };
  }, [clearBlockingHideTimeout, clearBlockingShowTimeout]);

  const value = useMemo(
    () => ({ isNavigating, isBlocking, startNavigation, stopNavigation, setBlockingTask, runBlockingTask }),
    [isBlocking, isNavigating, runBlockingTask, setBlockingTask, startNavigation, stopNavigation]
  );

  const overlayVisible = showNavigationOverlay || showBlockingOverlay;
  const overlayLabel = showBlockingOverlay ? blockingMessage : t('common.loading');

  return (
    <NavigationLoaderContext.Provider value={value}>
      {children}
      <div
        aria-hidden="true"
        className={[
          'pointer-events-none fixed inset-x-0 top-0 z-[171] h-1 overflow-hidden transition-opacity duration-150',
          isNavigating ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      >
        <div className="absolute inset-0 bg-emerald-500/10" />
        <div className="navigation-loader-bar absolute inset-y-0 w-1/3 min-w-28 rounded-full bg-gradient-to-r from-emerald-300 via-emerald-500 to-emerald-300 shadow-[0_0_18px_rgba(34,197,94,0.45)]" />
      </div>
      <div
        aria-live="polite"
        aria-busy={overlayVisible}
        className={[
          'fixed inset-0 z-[170] flex items-center justify-center bg-[#080811]/34 backdrop-blur-[3px] transition-all duration-200',
          overlayVisible ? 'pointer-events-auto opacity-100' : 'pointer-events-none invisible opacity-0',
        ].join(' ')}
      >
        <div className="rounded-3xl border border-white/10 bg-[#10101a]/88 px-8 py-7 shadow-[0_24px_80px_rgba(0,0,0,0.42)]">
          <LoadingGif
            label={overlayLabel}
            size={64}
            orientation="column"
            priority
            textClassName="text-sm font-medium text-zinc-200"
          />
        </div>
      </div>
    </NavigationLoaderContext.Provider>
  );
}

export function useNavigationLoader() {
  return useContext(NavigationLoaderContext);
}

export function useGlobalBlockingOverlay(active: boolean, message: string) {
  const { setBlockingTask } = useNavigationLoader();
  const keyRef = useRef(`blocking-overlay-${Math.random().toString(36).slice(2, 10)}`);

  useEffect(() => {
    setBlockingTask(keyRef.current, active, message);
    return () => {
      setBlockingTask(keyRef.current, false);
    };
  }, [active, message, setBlockingTask]);
}